import { useCallback, useEffect, useRef, useState } from 'react';
import { ImagePlus, Star, Trash2, Upload } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Miniatura from './Miniatura';

// Galería de un producto: arrastrar/soltar o elegir archivo, marcar la
// principal y dar de baja.
//
// Dos decisiones que se notan al usarlo:
//
//  - Se sube UNA por una y cada resultado se informa por separado. Un lote
//    de cinco donde la tercera pesa 9 MB no debe abortar las otras cuatro,
//    y quien lo hace tiene que saber exactamente cuál falló y por qué.
//  - La principal se marca con estrella y se dice qué significa. "Es la que
//    aparece en el catálogo y en el escáner" es la única explicación que
//    hace falta; sin ella, la estrella es decoración.
const MAX_MB = 5;

export default function GaleriaImagenes({ sku, editable = true }) {
  const [imagenes, setImagenes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [errores, setErrores] = useState([]);
  const [encima, setEncima] = useState(false);
  const entrada = useRef(null);

  const recargar = useCallback(async () => {
    try {
      setImagenes(await api.get(`/productos/${sku}/imagenes`));
    } catch {
      setImagenes([]);
    } finally {
      setCargando(false);
    }
  }, [sku]);

  useEffect(() => { recargar(); }, [recargar]);

  async function subirVarias(archivos) {
    if (!archivos?.length) return;
    setSubiendo(true);
    setErrores([]);
    const fallos = [];
    for (const archivo of Array.from(archivos)) {
      // Se comprueba el tamaño antes de enviar: subir 9 MB por la red para
      // que el servidor los rechace es tiempo del usuario tirado.
      if (archivo.size > MAX_MB * 1024 * 1024) {
        fallos.push(`${archivo.name}: pesa ${(archivo.size / 1048576).toFixed(1)} MB y el máximo es ${MAX_MB} MB.`);
        continue;
      }
      try {
        await api.subir(`/productos/${sku}/imagenes`, archivo);
      } catch (e) {
        fallos.push(`${archivo.name}: ${e instanceof ErrorApi ? e.message : 'no se pudo subir.'}`);
      }
    }
    setErrores(fallos);
    setSubiendo(false);
    recargar();
  }

  async function marcarPrincipal(id) {
    await api.patch(`/productos/${sku}/imagenes/${id}`, {});
    recargar();
  }

  async function eliminar(id) {
    await api.del(`/productos/${sku}/imagenes/${id}`);
    recargar();
  }

  return (
    <div className="space-y-3">
      {editable && (
        <div
          onDragOver={(e) => { e.preventDefault(); setEncima(true); }}
          onDragLeave={() => setEncima(false)}
          onDrop={(e) => {
            e.preventDefault();
            setEncima(false);
            subirVarias(e.dataTransfer.files);
          }}
          className={`border-2 border-dashed rounded-xl p-5 text-center transition-colors ${
            encima ? 'border-[var(--color-marca)] bg-blue-50' : 'border-gray-300 bg-gray-50'
          }`}
        >
          <ImagePlus className="h-7 w-7 mx-auto text-gray-500" />
          <p className="text-sm font-semibold text-gray-700 mt-2">
            Arrastra las fotos aquí
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            JPG, PNG o WebP · hasta {MAX_MB} MB cada una · máximo 8 por producto
          </p>
          <button
            type="button"
            onClick={() => entrada.current?.click()}
            disabled={subiendo}
            className="mt-3 inline-flex items-center gap-1.5 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-60 text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg"
          >
            <Upload className="h-4 w-4" />
            {subiendo ? 'Subiendo…' : 'Elegir archivos'}
          </button>
          <input
            ref={entrada}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => { subirVarias(e.target.files); e.target.value = ''; }}
          />
        </div>
      )}

      {errores.length > 0 && (
        <ul className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5 space-y-1">
          {errores.map((t) => <li key={t}>{t}</li>)}
        </ul>
      )}

      {cargando ? (
        <p className="text-sm text-gray-500">Cargando imágenes…</p>
      ) : imagenes.length === 0 ? (
        !editable && <p className="text-sm text-gray-500">Este producto no tiene fotos.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
            {imagenes.map((img) => (
              <figure key={img.id} className="relative group">
                <Miniatura src={img.url_mini} nombre={sku} tamano="xl" className="aspect-square" />
                {img.principal && (
                  <span className="absolute top-1 left-1 bg-amber-400 text-amber-900 rounded-md px-1.5 py-0.5 text-[10px] font-bold inline-flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current" /> Principal
                  </span>
                )}
                {editable && (
                  <div className="absolute inset-x-1 bottom-1 flex justify-end gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    {!img.principal && (
                      <button
                        type="button"
                        onClick={() => marcarPrincipal(img.id)}
                        title="Hacer principal"
                        className="bg-white/95 border border-gray-300 rounded-md p-1.5 hover:bg-white"
                      >
                        <Star className="h-3.5 w-3.5 text-amber-700" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => eliminar(img.id)}
                      title="Dar de baja esta imagen"
                      className="bg-white/95 border border-gray-300 rounded-md p-1.5 hover:bg-white"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </button>
                  </div>
                )}
              </figure>
            ))}
          </div>
          {editable && (
            <p className="text-xs text-gray-500">
              La <strong>principal</strong> es la que aparece en el catálogo, en la búsqueda y
              en la ficha que abre el escáner. Dar de baja una imagen no borra el archivo:
              deja de mostrarse y queda registrado quién la quitó.
            </p>
          )}
        </>
      )}
    </div>
  );
}
